<template>
    <Panel
        class="!bg-slate-50 !shadow-none px-4 py-6 md:px-6"
        :title="$t('search.modules')"
    >
        <template #title-prefix>
            <i class="icon icon-course mr-2"></i>
        </template>
        <template
            v-if="minMD"
            #header-options
        >
            <BtnCta
                v-if="courses.length && !isLoading"
                :right-icon="expanded ? 'chevron-up' : 'chevron-down'"
                size="sm"
                :label="expanded ? $t('search.see_less') : $t('search.see_more')"
                variant="tertiary"
                :data-testid="expanded ? 'course-section_see-less_button' : 'course-section_see-more_button'"
                @click="toggleExpanded"
            />
        </template>
        <component
            :is="minMD ? 'div' : 'Carousel'"
            no-edge-fade
            :class="{ 'grid grid-cols-3 gap-3': minMD }"
        >
            <transition-group
                name="fade"
                mode="out-in"
            >
                <CardPreviewCourse
                    v-for="course in itemsToShow"
                    :key="course.id"
                    class="min-w-44 w-44 md:w-auto"
                    :course-name="course.name"
                    :course-id="course.id"
                    :uni-name="course.university_name"
                    :uni-id="course.university_id"
                    :num-posts="course.questions_count"
                    :num-documents="course.documents_count"
                    :num-users="course.students_count"
                    :enable-links="!!(course.university_name && course.university_id)"
                />
                <template v-if="(!courses.length || minMD) && isLoading">
                    <Skeleton
                        v-for="i in 3"
                        :key="i"
                        appearance="card"
                        class="min-w-44 w-44 md:w-auto h-30"
                    />
                </template>
            </transition-group>
            <Btn
                v-if="maxSM && hasMore && courses.length"
                :label="$t('search.see_more')"
                :loading="isLoading"
                variant="filled"
                right-icon="arrow-right"
                color="blue-200"
                content-color="primary"
                class="w-44"
                @click="loadMore"
            />
        </component>
        <div
            v-if="minMD && hasMore && expanded"
            class="flex justify-center"
        >
            <BtnCta
                class="self-center mt-2"
                :label="$t('search.see_more')"
                variant="tertiary"
                size="sm"
                :disabled="isLoading"
                right-icon="chevron-down"
                data-testid="course-section_see-more_button"
                @click="loadMore"
            />
        </div>
        <slot name="footer"></slot>
    </Panel>
</template>

<script lang="ts">
import type { SearchCourseResource } from '@/api/gateway/legacy-api/resources/SearchCourseResource';
import { type PropType, ref } from 'vue';
import { defineComponent } from 'vue';
import CardPreviewCourse from '@/components/CardPreviewCourse';
import Panel from '@/components/Panel';
import Carousel from '@/components/CarouselNew';
import { useScreenSize } from '@/composables/screenSize';

export default defineComponent({
    name: 'PanelCourses',
    components: {
        CardPreviewCourse,
        Panel,
        Carousel,
    },
    props: {
        /**
         * Set of the loading state
         */
        isLoading: {
            type: Boolean,
            default: false,
        },
        /**
         * List of courses as an array
         */
        courses: {
            type: Array as PropType<SearchCourseResource[]>,
            required: true,
        },
        /**
         * Indicates if has more results to toggle the button
         */
        hasMore: {
            type: Boolean,
            default: false,
        },
    },
    emits: ['load-more'],
    setup() {
        const { minMD, maxSM } = useScreenSize();
        const expanded = ref(false);

        return {
            minMD,
            maxSM,
            expanded,
        };
    },
    computed: {
        itemsToShow(): SearchCourseResource[] {
            return this.expanded || this.maxSM ? this.courses : this.courses.slice(0, 3);
        },
    },
    methods: {
        toggleExpanded() {
            this.expanded = !this.expanded;
        },
        loadMore() {
            this.$emit('load-more');
        },
    },
});
</script>
